import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const CYCLE_MESSAGES = [
  "Checking sales tax nexus indicators...",
  "Reviewing privacy law readiness...",
  "Calculating your Readiness Score...",
  "Preparing your recommendations...",
];

/**
 * Full-screen overlay shown while an audit job runs.
 * Cycles subtext every 4s, animates a slow visual progress bar,
 * and shows a retry CTA if status === "failed".
 */
export function AuditRunOverlay({ status, errorMessage, onRetry, onCancel }) {
  const [msgIdx, setMsgIdx] = useState(0);
  const [progress, setProgress] = useState(8);

  useEffect(() => {
    if (status === "failed" || status === "complete") return;
    const cycle = setInterval(() => setMsgIdx((i) => (i + 1) % CYCLE_MESSAGES.length), 4000);
    return () => clearInterval(cycle);
  }, [status]);

  useEffect(() => {
    if (status === "failed" || status === "complete") return;
    // Asymptotic visual progress bar — climbs toward 92% but never reaches 100% until done.
    const tick = setInterval(() => {
      setProgress((p) => (p < 92 ? p + Math.max(0.4, (92 - p) * 0.04) : p));
    }, 500);
    return () => clearInterval(tick);
  }, [status]);

  const failed = status === "failed";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[var(--rg-bg)]/95 backdrop-blur-sm"
      data-testid="audit-run-overlay"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg mx-6 rounded-lg border border-[var(--rg-border)] bg-[var(--rg-bg)] p-8"
      >
        {!failed ? (
          <>
            <div className="flex justify-center mb-6">
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 rounded-full border-2 border-[var(--rg-border)]" />
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-[var(--rg-sage)] border-t-transparent"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-3 h-3 rounded-sm bg-[var(--rg-sage)]" />
                </div>
              </div>
            </div>

            <h2
              className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] text-center mb-2"
              data-testid="audit-overlay-title"
            >
              Analyzing your store across 50 tax jurisdictions and 15 privacy-scored states...
            </h2>

            <div className="h-6 mt-3 mb-6">
              <motion.p
                key={msgIdx}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-sm text-[var(--rg-text)] text-center"
                data-testid="audit-overlay-subtext"
              >
                {CYCLE_MESSAGES[msgIdx]}
              </motion.p>
            </div>

            <div className="h-1.5 bg-[var(--rg-surface)] border border-[var(--rg-border)] rounded-sm overflow-hidden mb-4">
              <motion.div
                className="h-full bg-[var(--rg-sage)]"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                data-testid="audit-overlay-progress"
              />
            </div>

            <p className="text-xs text-[var(--rg-muted)] text-center">
              This typically takes 15–30 seconds.
            </p>
          </>
        ) : (
          <>
            <div className="flex justify-center mb-5">
              <div className="w-14 h-14 rounded-full bg-[var(--rg-red-soft)] flex items-center justify-center border border-[var(--rg-red)]/40">
                <AlertCircle className="w-7 h-7 text-[var(--rg-red)]" />
              </div>
            </div>
            <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] text-center mb-3" data-testid="audit-overlay-failed-title">
              We couldn&apos;t complete your audit
            </h2>
            <p className="text-sm text-[var(--rg-text)] text-center mb-2">
              The audit could not be completed. Please try again. If the problem continues, contact support.
            </p>
            {errorMessage && (
              <p className="text-[10px] font-mono text-[var(--rg-muted)] text-center mb-5 break-words">
                {errorMessage}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={onRetry}
                className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold"
                data-testid="audit-overlay-retry"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
              {onCancel && (
                <Button
                  variant="outline"
                  onClick={onCancel}
                  className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]"
                  data-testid="audit-overlay-cancel"
                >
                  Cancel
                </Button>
              )}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

/**
 * Polls `/api/audit/status/:job_id` every 3 seconds.
 * Returns { status, errorMessage, result } via callback.
 * Total timeout: 3 minutes — after which the job is treated as failed.
 *
 * Usage:
 *   const { status, errorMessage, result, start, reset } = useAuditJobPolling(api);
 *   start(jobId);
 */
export function useAuditJobPolling(api) {
  const [status, setStatus] = useState(null); // null | pending | processing | complete | failed
  const [errorMessage, setErrorMessage] = useState(null);
  const [result, setResult] = useState(null);
  const [jobId, setJobId] = useState(null);

  useEffect(() => {
    if (!jobId) return;
    if (status === "complete" || status === "failed") return;

    let cancelled = false;
    const startTs = Date.now();
    const TIMEOUT_MS = 3 * 60 * 1000; // 3 min

    const poll = async () => {
      if (cancelled) return;
      if (Date.now() - startTs > TIMEOUT_MS) {
        setStatus("failed");
        setErrorMessage("Audit took longer than expected. Please try again.");
        return;
      }
      try {
        const { data } = await api.get(`/audit/status/${jobId}`);
        if (cancelled) return;
        setStatus(data.status);
        if (data.status === "complete") {
          setResult(data.result);
        } else if (data.status === "failed") {
          setErrorMessage(data.error_message || "Audit failed.");
        } else {
          setTimeout(poll, 3000);
        }
      } catch (e) {
        if (cancelled) return;
        setStatus("failed");
        setErrorMessage(e?.response?.data?.detail || "Could not reach the audit service.");
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [jobId, status, api]);

  const start = (id) => {
    setStatus("pending");
    setErrorMessage(null);
    setResult(null);
    setJobId(id);
  };
  const reset = () => {
    setStatus(null);
    setErrorMessage(null);
    setResult(null);
    setJobId(null);
  };

  return { status, errorMessage, result, jobId, start, reset };
}
